import { useCallback, useMemo } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  JournalEntryType,
  useCreateJournal,
  useCreateJournalEntry,
  useCreateJournalPhotoEntry,
  useJournalTitleGenerationStore,
} from '@zendia/frontend-shared'
import { DateTime } from 'luxon'
import { axiosRequest } from '../../../../utilities/axiosClient'
import {
  JournalEntryForm,
  type JournalFormResult,
} from '../../../../components/Journal/JournalEntryForm'

export const Route = createFileRoute('/_authenticated/journal/new/form')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
})

function RouteComponent() {
  const router = useRouter()
  const queryClient = useQueryClient()

  // Destructured because a mutation's mutateAsync is a stable reference while
  // the result object around it is new every render.
  const { mutateAsync: createJournal } = useCreateJournal(axiosRequest)
  const { mutateAsync: createJournalEntry } = useCreateJournalEntry(axiosRequest)
  const { mutateAsync: createJournalPhotoEntry } = useCreateJournalPhotoEntry(axiosRequest)
  const markTitlePending = useJournalTitleGenerationStore((state) => state.markPending)

  const today = useMemo(() => {
    return DateTime.now().toLocaleString({
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    })
  }, [])

  const onDiscard = useCallback(() => {
    router.history.back()
  }, [router])

  const onSave = useCallback(
    async ({ title, text, labels, addedFiles }: JournalFormResult) => {
      const response = await createJournal({
        // An untitled journal is titled by its date, which journalTitle() renders.
        title: title || DateTime.now().toISODate(),
        labels,
      })

      // A rejected request throws, but a response failing its schema does not,
      // so check before navigating away - otherwise the entry is lost silently.
      if (!response.isSuccess || !response.data) {
        throw new Error('Failed to save journal entry. Please try again.')
      }

      const journalId = response.data.journalId
      const textEntry = text?.trim()

      try {
        if (textEntry) {
          await createJournalEntry({
            journalId,
            type: JournalEntryType.Text,
            value: textEntry,
          })

          // Saving the text queues tagging, which writes a suggested title for an
          // untitled journal. Tagging reads only the text, so a photo-only journal
          // never gets one and is not marked.
          if (!title?.trim()) {
            markTitlePending(journalId)
          }
        }

        if (addedFiles.length > 0) {
          await Promise.all(
            addedFiles.map((file) =>
              createJournalPhotoEntry({
                journalId,
                value: file,
                caption: file.name || '',
              }),
            ),
          )
        }
      } finally {
        // The list is cached (staleTime), so it must be told about the new journal.
        // In finally, so a partial save still shows the journal that was created.
        await queryClient.invalidateQueries({ queryKey: ['journal'], exact: true })
      }

      onDiscard()
    },
    [
      createJournal,
      createJournalEntry,
      createJournalPhotoEntry,
      markTitlePending,
      onDiscard,
      queryClient,
    ],
  )

  return (
    <JournalEntryForm
      headerContent={today}
      saveLabel="Save journal"
      onSave={onSave}
      onDiscard={onDiscard}
    />
  )
}
